import React from 'react';

/**
 * 通用的文件展示组件
 * 支持基金文件、基金公告、下载等不同类型的文件展示
 * @param {Object} props - 组件属性
 * @param {Array} props.items - 文件列表数据
 * @param {string} props.title - 标题
 * @param {string} props.type - 组件类型 ('files' | 'notices' | 'downloads')
 * @param {number} props.defaultCount - 默认显示数量，默认为5
 * @param {boolean} props.showDate - 是否显示日期，默认为true
 * @param {string} props.dateFormat - 日期格式，默认为'slice(0,10)'
 * @param {function} props.onItemClick - 点击文件时的回调函数
 * @param {boolean} props.showCollapse - 是否显示收起功能，默认为true
 * @param {boolean} props.showTitleWhenEmpty - 数据为空时是否显示标题，默认为true
 */
class FileListComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAll: false,
      visibleCount: props.defaultCount || 5
    };
  }

  handleViewMore = () => {
    this.setState({ showAll: true });
    // 可以在这里添加回调，用于统计或跟踪
    if (this.props.onViewMore) {
      this.props.onViewMore(this.props.type);
    }
  }

  handleCollapse = () => {
    this.setState({ showAll: false });
    // 可以在这里添加回调，用于统计或跟踪
    if (this.props.onCollapse) {
      this.props.onCollapse(this.props.type);
    }
    
    // 滚动到组件顶部（可选）
    if (this.componentRef) {
      this.componentRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleItemClick = (item, index) => {
    // 如果有自定义的点击处理函数，则调用
    if (this.props.onItemClick) {
      this.props.onItemClick(item, index, this.props.type);
    }
  }

  formatDate = (dateString) => {
    const { dateFormat } = this.props;
    if (!dateString) return '';
    
    if (dateFormat === 'full') {
      return dateString;
    } else if (dateFormat === 'slice') {
      return dateString.slice(0, 10);
    } else if (typeof dateFormat === 'function') {
      return dateFormat(dateString);
    } else {
      // 默认格式：取前10个字符
      return dateString.slice(0, 10);
    }
  }

  renderItems = () => {
    const { items, type, showDate = true } = this.props;
    const { showAll, visibleCount } = this.state;
    
    if (!items || items.length === 0) {
      return (
        <tr>
          <td colSpan="4" className="no-data">暂无数据</td>
        </tr>
      );
    }

    const displayItems = showAll ? items : items.slice(0, visibleCount);

    return displayItems.map((item, index) => {
      const downloadUrl = `/active-service/HK-BOCIM/download/${item.ID}`;
      
      return (
        <tr key={`${type}-${item.ID || index}`}>
          <td style={{width: '5%'}}>
            <img 
              alt="下载" 
              src="./images/download.gif" 
              width="21" 
              height="25"
            />
          </td>
          {showDate && (
            <td width="20%">
              {this.formatDate(item.PUBLISH_DATE)}
            </td>
          )}
          <td colSpan={showDate ? "2" : "3"}>
            <a 
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => this.handleItemClick(item, index)}
            >
              {item.PUBLISH_NAME}
            </a>
          </td>
        </tr>
      );
    });
  }

  renderFooter = () => {
    const { items, type, showCollapse = true } = this.props;
    const { showAll, visibleCount } = this.state;
    
    if (!items || items.length === 0 || items.length <= visibleCount) {
      // 如果数据量为空或小于等于默认显示数量，不显示任何按钮
      return null;
    }

    const hasMore = !showAll;
    const hasCollapse = showAll && showCollapse;

    return (
      <div className="action-footer">
        {hasMore && (
          <a 
            className="action-btn view-more-btn" 
            onClick={this.handleViewMore}
            aria-label={`查看全部${this.props.title || ''}`}
          >
            查看全部 ({items.length - visibleCount} 条)
          </a>
        )}
        {hasCollapse && (
          <a 
            className="action-btn collapse-btn" 
            onClick={this.handleCollapse}
            aria-label={`收起${this.props.title || ''}`}
          >
            收起
          </a>
        )}
      </div>
    );
  }

  render() {
    const { 
      items, 
      title, 
      type, 
      className = '',
      showDate = true,
      showTitleWhenEmpty = true // 新增：数据为空时是否显示标题
    } = this.props;
    
    const { showAll, visibleCount } = this.state;
    
    // 如果没有数据，仍然渲染组件，显示标题和暂无数据提示
    // 或者根据showTitleWhenEmpty参数决定是否显示
    if (!items || items.length === 0) {
      // 如果不显示标题且数据为空，则不渲染组件
      if (!showTitleWhenEmpty) {
        return null;
      }
      
      // 显示标题和暂无数据提示
      return (
        <div 
          className={`file-list-component ${type}-section ${className} empty-section`}
          ref={ref => this.componentRef = ref}
        >
          {title && <h2 className="section-title">{title}</h2>}
          <table className="dltable file-table">
            <tbody>
              <tr>
                <td colSpan="4" className="no-data">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div 
        className={`file-list-component ${type}-section ${className}`}
        ref={ref => this.componentRef = ref}
      >
        {title && <h2 className="section-title">{title}</h2>}
        <table className="dltable file-table">
          <tbody>
            {this.renderItems()}
          </tbody>
        </table>
        {this.renderFooter()}
      </div>
    );
  }
}

export default FileListComponent;